import { SelectionModel } from '@angular/cdk/collections';
import { NgClass, KeyValuePipe } from '@angular/common';
import { Component, EventEmitter, Input, Output, ViewChild, inject, NgZone } from '@angular/core';
import { FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatCheckbox, MatCheckboxChange } from '@angular/material/checkbox';
import { MatOptionSelectionChange } from '@angular/material/core';
import { MatDatepickerInput, MatDatepickerToggle, MatDatepickerToggleIcon, MatDatepicker } from '@angular/material/datepicker';
import { MatExpansionPanel, MatExpansionPanelHeader, MatExpansionPanelTitle } from '@angular/material/expansion';
import { MatFormField, MatError, MatLabel, MatSuffix } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { MatSelect, MatOption, MatOptgroup } from '@angular/material/select';
import { MatSelectChange } from '@angular/material/select';
import {
  MatTable,
  MatColumnDef,
  MatHeaderCellDef,
  MatHeaderCell,
  MatCellDef,
  MatCell,
  MatHeaderRowDef,
  MatHeaderRow,
  MatRowDef,
  MatRow,
} from '@angular/material/table';
import { MatTooltip } from '@angular/material/tooltip';
import { CommonIP, DocumentSubType, IP, NestedSubType, RelatedFileGroup } from '@model/ftf-api.model';
import { getTrifoldDocIcon } from '@utils/file-utils';
import { SelectionFormArray } from '@utils/selection-form-array';

import { UploadTableFormControl, TableEvent, TrifoldItemEvent } from './upload-table.model';
import { DocSubTypeSelectComponent } from '../../../shared/components/doc-sub-type-select/doc-sub-type-select.component';
import { IpIconComponent } from '../../../shared/components/ip-icon/ip-icon.component';

@Component({
  selector: 'app-upload-table-presentation',
  standalone: true,
  templateUrl: './upload-table.presentation.component.html',
  styleUrls: ['./upload-table.presentation.component.scss'],
  imports: [
    MatExpansionPanel,
    MatExpansionPanelHeader,
    MatExpansionPanelTitle,
    MatTooltip,
    MatIcon,
    IpIconComponent,
    MatTable,
    MatColumnDef,
    MatHeaderCellDef,
    MatHeaderCell,
    MatCheckbox,
    MatCellDef,
    MatCell,
    FormsModule,
    ReactiveFormsModule,
    MatFormField,
    NgClass,
    MatInput,
    MatError,
    MatSelect,
    MatOption,
    MatLabel,
    MatDatepickerInput,
    MatDatepickerToggle,
    MatSuffix,
    MatDatepickerToggleIcon,
    MatDatepicker,
    MatOptgroup,
    DocSubTypeSelectComponent,
    MatHeaderRowDef,
    MatHeaderRow,
    MatRowDef,
    MatRow,
    KeyValuePipe,
  ],
})
export class UploadTablePresentationComponent {
  private ngZone = inject(NgZone);

  @ViewChild(MatTable, { static: true }) table!: MatTable<unknown>;

  @Input({ required: true }) form!: FormGroup<UploadTableFormControl>;
  @Input({ required: true }) relFileGroup!: RelatedFileGroup;
  @Input({ required: true }) displayedColumns!: string[];
  @Input({ required: true }) dataSource!: FormGroup[];
  @Input({ required: true }) selection!: SelectionModel<FormGroup>;
  @Input({ required: true }) subTypesByDocType!: NestedSubType;
  @Input({ required: true }) supportedTrifolds!: { [key: string]: string };
  @Input() item: CommonIP | null = null;
  @Input({ required: true }) ipType!: IP.TypeEnum;
  @Input({ required: true }) identifier!: string;
  @Input({ required: true }) files!: SelectionFormArray<FormGroup>;

  @Output() openFTF = new EventEmitter<TrifoldItemEvent>();
  @Output() openIPMS = new EventEmitter<TrifoldItemEvent>();
  @Output() closeTable = new EventEmitter<TableEvent>();
  @Output() trifoldChange = new EventEmitter<{ event: MatOptionSelectionChange; file: FormGroup }>();
  @Output() subTypeChange = new EventEmitter<{ event: DocumentSubType | null; file: FormGroup }>();
  @Output() selectChange = new EventEmitter<{ event: MatCheckboxChange; row: FormGroup }>();
  @Output() dateAppliedToAll = new EventEmitter<Date>();
  @Output() subTypeAppliedToAll = new EventEmitter<MatSelectChange>();
  @Output() booleanAppliedToAll = new EventEmitter<{ controlName: string; value: boolean }>();
  @Output() trifoldAppliedToAll = new EventEmitter<MatSelectChange>();
  @Output() allRowsToggled = new EventEmitter<MatCheckboxChange | null>();

  getTrifoldDocIcon(rec: FormGroup): string {
    return getTrifoldDocIcon(rec.value.name, false);
  }

  getTrifoldTypes(): { [key: string | number]: string } {
    return this.ipType === 'Reference' || this.ipType === 'CaseReference' ? IP.ReferenceTypes : this.supportedTrifolds;
  }

  isAllSelected(): boolean {
    const numSelected = this.selection.selected.length;
    const numRows = this.files.controls.length;
    return numSelected === numRows && numRows > 0;
  }

  isAllControlSelected(controlName: string): boolean | null {
    const { controls } = this.files;
    if (controls.length === 0) return false;
    const allSelected = controls.every((group) => group.get(controlName)?.value === true);
    const noneSelected = controls.every((group) => group.get(controlName)?.value === false);
    return allSelected ? true : noneSelected ? false : null;
  }

  toggleAllRows(event: MatCheckboxChange | null = null): void {
    this.allRowsToggled.emit(event);
  }

  onSelectChange(event: MatCheckboxChange, row: FormGroup): void {
    this.selectChange.emit({ event, row });
  }

  onTableEvent(event: MouseEvent, emitter: EventEmitter<TableEvent>): void {
    event.stopPropagation();
    this.ngZone.run(() => {
      emitter.emit({ identifier: this.identifier, ipType: this.ipType, form: this.form });
    });
  }

  onItemEvent(event: MouseEvent, emitter: EventEmitter<TrifoldItemEvent>): void {
    event.stopPropagation();
    if (this.item && this.ipType !== 'Reference') {
      this.ngZone.run(() => {
        emitter.emit({ item: this.item! });
      });
    }
  }

  onTrifoldChange(event: MatOptionSelectionChange, file: FormGroup): void {
    this.trifoldChange.emit({ event, file });
  }

  onSubTypeChange(event: DocumentSubType | null, file: FormGroup) {
    this.subTypeChange.emit({ event, file });
  }

  applyDateToAll(selectedDate: Date): void {
    this.dateAppliedToAll.emit(selectedDate);
  }

  applySubTypeToAll(event: MatSelectChange): void {
    this.subTypeAppliedToAll.emit(event);
  }

  applyBooleanToAll(controlName: string, value: boolean): void {
    this.booleanAppliedToAll.emit({ controlName, value });
  }

  applyTrifoldToAll(event: MatSelectChange): void {
    this.trifoldAppliedToAll.emit(event);
  }
}
